import React from 'react';
import { InfluenceScore, InfluenceCategory } from '../../services/influencesService';
import { isHexColor } from '../ui';

interface InfluenceLineChartProps {
  data: InfluenceScore[];
  categories: InfluenceCategory[];
  /** Kept for compatibility; the chart fills its container's width */
  width?: number;
  /** Kept for compatibility; the height follows the number of categories */
  height?: number;
  className?: string;
}

const GRID = [0, 25, 50, 75, 100];
const AXIS = [0, 50, 100];

/**
 * Influence score (0–100) per category as thin horizontal bars on a shared scale.
 * Each bar takes its influence category's colour (ink when none is set) on a hairline grid;
 * the highest score is emphasised through its label and value, not a different hue.
 */
export const InfluenceLineChart: React.FC<InfluenceLineChartProps> = ({ data, categories, className = '' }) => {
  if (!data.length || !categories.length) return null;

  const scoreByCategory = new Map<string, number>();
  const colorByCategory = new Map<string, string>();
  data.forEach((score) => {
    scoreByCategory.set(score.categoryId, score.score);
    if (isHexColor(score.category?.color)) colorByCategory.set(score.categoryId, score.category!.color);
  });

  const values = categories.map((category) => scoreByCategory.get(category.id));
  const known = values.filter((value): value is number => typeof value === 'number');
  const max = known.length ? Math.max(...known) : 0;
  // Emphasis only means something when the top score stands out
  const highlight = max > 0 && known.some((value) => value < max);

  const rowGrid = 'grid grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)_2rem] items-center gap-x-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_2rem]';

  return (
    <figure className={`font-ui ${className}`} aria-label="Influence by category">
      <ul>
        {categories.map((category, index) => {
          const value = values[index];
          const clamped = Math.max(0, Math.min(100, value ?? 0));
          const top = highlight && value === max;
          const color = isHexColor(category.color) ? category.color : colorByCategory.get(category.id);
          return (
            <li
              key={category.id}
              className={`${rowGrid} group h-8`}
              title={`${category.label}: ${typeof value === 'number' ? value : 'no score'}`}
            >
              <span className={`truncate text-[12px] ${top ? 'text-ink' : 'text-neutral-500'}`}>{category.label}</span>
              <span className="relative h-full" aria-hidden="true">
                {GRID.map((tick) => (
                  <span
                    key={tick}
                    className={`absolute inset-y-0 w-px ${tick === 0 ? 'bg-neutral-300' : 'bg-neutral-200'}`}
                    style={{ left: `${tick}%` }}
                  />
                ))}
                <span
                  className={`absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-r-[4px] transition-[width] duration-500 ease-out group-hover:opacity-80 ${
                    color ? '' : 'bg-ink'
                  }`}
                  style={{ width: `${clamped}%`, ...(color ? { backgroundColor: color } : {}) }}
                />
              </span>
              <span
                className={`text-right text-[12px] tabular-nums ${
                  typeof value === 'number' ? (top ? 'text-ink' : 'text-neutral-500') : 'text-neutral-300'
                }`}
              >
                {typeof value === 'number' ? value : '—'}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Scale */}
      <div className={`${rowGrid} mt-1 h-4`} aria-hidden="true">
        <span />
        <span className="relative h-full">
          {AXIS.map((tick) => (
            <span
              key={tick}
              className={`absolute top-0 text-[11px] tabular-nums text-neutral-400 ${
                tick === 0 ? '' : tick === 100 ? '-translate-x-full' : '-translate-x-1/2'
              }`}
              style={{ left: `${tick}%` }}
            >
              {tick}
            </span>
          ))}
        </span>
        <span />
      </div>
    </figure>
  );
};
